'use client';

import { useEffect, useRef, useState } from 'react';

import { UseIntersectionObserverOptions } from '../interfaces/component.interface';

export function useIntersectionObserver({
  threshold = 0,
  rootMargin = '0px',
  freezeOnceVisible = false,
}: UseIntersectionObserverOptions = {}) {
  const [entry, setEntry] = useState<IntersectionObserverEntry>();
  const [node, setNode] = useState<Element | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const frozen = entry?.isIntersecting && freezeOnceVisible;

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const updateEntry = ([entry]: IntersectionObserverEntry[]): void => {
    setEntry(entry);
  };

  useEffect(() => {
    const hasIOSupport = !!window.IntersectionObserver;

    if (!hasIOSupport || frozen || !node) return;

    const observerParams = { threshold, rootMargin };
    const currentObserver = new IntersectionObserver(updateEntry, observerParams);

    observer.current = currentObserver;
    currentObserver.observe(node);

    // eslint-disable-next-line consistent-return
    return () => { return currentObserver.disconnect(); };
  }, [node, threshold, rootMargin, frozen]);

  const prevNode = useRef<Element | null>(null);

  useEffect(() => {
    if (prevNode.current && node !== prevNode.current) {
      if (observer.current) {
        observer.current.disconnect();
      }
    }
    prevNode.current = node;
  }, [node]);

  return [setNode, entry] as const;
}

export function useInfiniteScroll(callback: () => void, hasMore: boolean, isLoading: boolean = false) {
  const [setRef, entry] = useIntersectionObserver({
    threshold: 0,
    rootMargin: '0px',
  });

  const callbackRef = useRef(callback);
  const hasCalledRef = useRef(false);
  callbackRef.current = callback;

  // Reset the component when it is mounted
  useEffect(() => {
    hasCalledRef.current = false;
  }, []);

  useEffect(() => {
    if (entry?.isIntersecting && hasMore && !isLoading && !hasCalledRef.current) {
      hasCalledRef.current = true;
      callbackRef.current();
    }
  }, [entry?.isIntersecting, hasMore, isLoading]);

  useEffect(() => {
    if (!isLoading && !entry?.isIntersecting) {
      hasCalledRef.current = false;
    }
  }, [isLoading, entry?.isIntersecting]);

  return setRef;
}
